import { scmpPlus } from "@product/scmp-sdk";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import { withRelay } from "relay-nextjs";
import type { RelayComponent } from "relay-nextjs";

import { AppFooter } from "~/components/app-footer";
import { FallbackLoading } from "~/components/fallback-loading";
import { LetterList } from "~/components/letter-list";
import { getLetterFromQueryString } from "~/components/letter-list/helpers";
import { PageSeo } from "~/components/meta";
import { TopicList } from "~/components/topic/topic-list";
import { data as appData } from "~/data";
import { createClientEnvironment } from "~/lib/relay/environment.client";
import { page } from "~/pages/topics";
import type { topicsPageQuery } from "~/queries/__generated__/topicsPageQuery.graphql";

import { MainHeading, PageContainer, PageContent, SubHeading } from "./styles";

const pageQuery = graphql`
  query topicsPageQuery(
    $name: String!
    $operator: StringFilterOperator = START_WITH
    $scmpPlusTypes: [String]
    $published: Boolean = true
    $enabled: Boolean = true
    $first: Int
    $after: String
    $applicationId: String!
  ) {
    ...topicListQuery @arguments(scmpPlusTypes: $scmpPlusTypes)
  }
`;

const TopicsPage: RelayComponent<unknown, topicsPageQuery> = ({ preloadedQuery }) => {
  const { query } = useRouter();
  const data = usePreloadedQuery(pageQuery, preloadedQuery);
  const selectedLetter = getLetterFromQueryString(query.char);

  const handleGetPageRoute = useMemo(() => page.route, []);
  return (
    <>
      <PageContainer>
        <PageContent>
          <PageSeo title="All Topics" />
          <MainHeading>Topics</MainHeading>
          <LetterList route={handleGetPageRoute} />
          <SubHeading>Topic - {selectedLetter}</SubHeading>
          <TopicList reference={data} />
        </PageContent>
      </PageContainer>
      <AppFooter />
    </>
  );
};

TopicsPage.displayName = "TopicsPage";

export default withRelay(TopicsPage, pageQuery, {
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("~/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  variablesFromContext(context) {
    return {
      applicationId: appData.application.scmp.entityId,
      first: 200,
      name: getLetterFromQueryString(context.query.char),
      scmpPlusTypes: [scmpPlus.topicType.newsAgenda],
    };
  },
});
